// Teams PR A: in-app team governance (board 6). The database decides everything (migration 235 RLS):
//   Team Owner  - set only by a Tenant Admin in the Admin Center; manages Team Admins, Members and team settings.
//   Team Admin  - adds / removes Members, only as the owner's team settings allow; cannot touch owners, admins or company limits.
//   Member      - uses the team space (sees the roster).
// These helpers only decide which controls a screen offers; a refused write still shows the database's own message.

export const TEAM_ROLE_WORDS = Object.freeze({ owner: 'Team Owner', admin: 'Team Admin', member: 'Member' });

export const DEFAULT_TEAM_SETTINGS = Object.freeze({ admins_can_add_members: true, admins_can_remove_members: true });

/** What the signed-in person may do in this team. */
export function teamPermissions(myRole, teamSettings = DEFAULT_TEAM_SETTINGS) {
    const settings = { ...DEFAULT_TEAM_SETTINGS, ...(teamSettings || {}) };
    const owner = myRole === 'owner';
    const admin = myRole === 'admin';
    return {
        canAddMembers: owner || (admin && settings.admins_can_add_members),
        canRemoveMembers: owner || (admin && settings.admins_can_remove_members),
        canManageAdmins: owner,
        canChangeSettings: owner,
    };
}

export async function loadTeamSpaces(client) {
    const { data, error } = await client.rpc('team_spaces_list');
    if (error) throw error;
    return data || [];
}

export async function loadTeam(client, teamId) {
    const [members, settings, people] = await Promise.all([
        client.from('org_team_members').select('id, user_id, team_role').eq('team_id', teamId),
        client.from('org_team_settings').select('admins_can_add_members, admins_can_remove_members').eq('team_id', teamId).maybeSingle(),
        client.rpc('team_candidate_people'),
    ]);
    const failure = [members, settings, people].find((result) => result.error);
    if (failure) throw failure.error;
    const names = new Map((people.data || []).map((person) => [person.user_id, person.full_name]));
    const order = { owner: 0, admin: 1, member: 2 };
    const roster = (members.data || [])
        .map((row) => ({ ...row, full_name: names.get(row.user_id) || 'Unnamed person' }))
        .sort((a, b) => (order[a.team_role] - order[b.team_role]) || a.full_name.localeCompare(b.full_name));
    return { roster, settings: { ...DEFAULT_TEAM_SETTINGS, ...(settings.data || {}) }, people: people.data || [] };
}

// A write the database refuses under row-level security can come back as "no rows" instead of an error, so writes ask for the
// changed rows back and treat none as a refusal.
async function must(promise) {
    const { data, error } = await promise;
    if (error) throw error;
    if (Array.isArray(data) && data.length === 0) throw new Error('You do not have permission to make that change.');
}

export function addTeamMember(client, orgId, teamId, userId, role = 'member') {
    return must(client.from('org_team_members').insert({ org_id: orgId, team_id: teamId, user_id: userId, team_role: role }).select('id'));
}

export function setTeamRole(client, membershipId, role) {
    return must(client.from('org_team_members').update({ team_role: role }).eq('id', membershipId).select('id'));
}

export function removeTeamMember(client, membershipId) {
    return must(client.from('org_team_members').delete().eq('id', membershipId).select('id'));
}

export function saveTeamSettings(client, orgId, teamId, settings) {
    return must(client.from('org_team_settings').upsert({
        team_id: teamId, org_id: orgId,
        admins_can_add_members: Boolean(settings.admins_can_add_members),
        admins_can_remove_members: Boolean(settings.admins_can_remove_members),
    }, { onConflict: 'team_id' }).select('team_id'));
}
